<template>
  <Screen
    v-if="isUserCreatorInGroup || isProjectVisibleToSales"
    v-loading="!boardSlides"
    collection-overview-switch
    left-transparent
    center-transparent
    right-transparent
    center-especial
    full-screen
    back-button
    :user="user"
    :group-id="selectedGroupId"
    :season-id="selectedSeasonId"
    :back-button-route="backButtonRoute"
    element-loading-text="Loading boards..."
  >
    <div slot="header-center" />

    <aside
      slot="screen-left"
      class="project__left"
    >
      <div
        v-if="hasMultipleBoards"
        class="project__boards-navigation"
        @click="showPreviousSlide"
      >
        <ElButton
          circle
          class="project__boards-navigation-prev"
        >
          <i class="el-icon-arrow-left" />
        </ElButton>
      </div>
    </aside>

    <section
      v-if="project"
      slot="screen-center"
      v-loading="!hasBoards"
      class="project__center"
    >
      <section class="project__boards">
        <TransitionGroup
          v-if="hasBoards"
          tag="div"
          class="project__slider"
        >
          <div
            v-for="board in boardSlides"
            :key="board.id"
            class="project__board"
            :data-id="board.id"
          >
            <BoardPreview
              presentation-mode
              :template-id="board.layout"
              :board-id="board.id"
              :image-resolution="imageResolution(board.layout)"
            />
          </div>
        </TransitionGroup>

        <div
          v-else
          class="project__empty"
        >
          No boards available yet...
        </div>
      </section>
    </section>

    <aside
      slot="screen-right"
      class="project__right"
    >
      <div
        v-if="hasMultipleBoards"
        class="project__boards-navigation"
        @click="showNextSlide"
      >
        <ElButton
          circle
          class="project__boards-navigation-next"
        >
          <i class="el-icon-arrow-right" />
        </ElButton>
      </div>
    </aside>
  </Screen>
</template>

<script>
import { mapActions } from 'vuex'

import { Workspace } from '@/mixins/workspace'
import { Projects } from '@/mixins/projects'
import Utils from '@/services/utils'

import { imageResolutions } from '@/constants/image'

import Screen from '@/components/Screen.vue'
import BoardPreview from '@/components/project/BoardPreview.vue'

export default {
  name: 'ProjectPresent',

  components: {
    Screen,
    BoardPreview
  },

  mixins: [Workspace, Projects],

  data: () => ({
    boardSlides: null,
    activeBoardId: null,
    boardsLoaded: {}
  }),

  computed: {
    /**
     * @returns {boolean}
     */
    hasMultipleBoards () {
      return this.boards.length > 1
    },

    /**
     * @returns {boolean}
     */
    isProjectVisibleToSales () {
      return this.project ? this.project.visible_to_sales : false
    },

    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.selectedGroupId)
    },

    /**
     * @returns {string}
     */
    backButtonRoute () {
      const previousRouteName = this.$router.previousRoute.name

      if (previousRouteName === 'Create') {
        return `/projects/${this.projectId}/create`
      } else {
        return `/projects/${this.projectId}/boards`
      }
    }
  },

  watch: {
    /**
     */
    activeBoardId () {
      this.fetchActiveAndSurroundingBoards()
    }
  },

  async created () {
    await this.fetchProject({
      projectId: this.projectId
    })

    this.updateSelectedGroupIdAndSelectedSeasonId(this.project)

    this.initProject()
  },

  methods: {
    ...mapActions(['fetchProject', 'fetchBoard']),

    /**
     */
    initProject () {
      if (this.boards.length > 0) {
        const activeBoardId = this.getActiveBoardIdDefault(this.projectId)
        const activeBoardIndex = this.boards.findIndex(
          board => board.id === activeBoardId
        )

        this.boardSlides = [
          ...this.boards.slice(activeBoardIndex),
          ...this.boards.slice(0, activeBoardIndex)
        ]
        this.activeBoardId = activeBoardId
      }
    },

    /**
     */
    fetchActiveAndSurroundingBoards () {
      const listIndex = this.getActiveBoardListIndex()

      if (listIndex > -1) {
        this.fetchBoardDetailedByListIndex(listIndex)
        this.fetchPreviousBoard(listIndex)
        this.fetchNextBoard(listIndex)
      }
    },

    /**
     * @param {number} listIndex
     */
    fetchPreviousBoard (listIndex) {
      let previousBoardListIndex = null

      if (listIndex === 0) {
        previousBoardListIndex = this.boardSlides.length - 1
      } else {
        previousBoardListIndex = listIndex - 1
      }

      if (listIndex !== previousBoardListIndex) {
        this.fetchBoardDetailedByListIndex(previousBoardListIndex)
      }
    },

    /**
     * @param {number} listIndex
     */
    fetchNextBoard (listIndex) {
      let nextBoardListIndex = null

      if (listIndex === this.boardSlides.length - 1) {
        nextBoardListIndex = 0
      } else {
        nextBoardListIndex = listIndex + 1
      }

      if (listIndex !== nextBoardListIndex) {
        this.fetchBoardDetailedByListIndex(nextBoardListIndex)
      }
    },

    /**
     * @param {number} listIndex
     */
    async fetchBoardDetailedByListIndex (listIndex) {
      const board = this.boards[listIndex]
      const boardId = board ? board.id : null

      if (boardId && !this.boardsLoaded[boardId]) {
        await this.fetchBoard({
          projectId: this.projectId,
          boardId
        })

        this.boardsLoaded[boardId] = true
      }
    },

    /**
     * @param   {number} boardLayout
     *
     * @returns {string}
     */
    imageResolution (boardLayout) {
      if (boardLayout <= 1) {
        return imageResolutions.STYLE_FULL
      } else {
        return imageResolutions.STYLE
      }
    },

    /**
     * @returns {number}
     */
    getActiveBoardListIndex () {
      return this.boards.findIndex(
        board => board.id === parseInt(this.activeBoardId)
      )
    },

    /**
     */
    showNextSlide () {
      const board = this.boardSlides.shift()

      this.boardSlides = this.boardSlides.concat(board)
      this.saveActiveBoardId(this.boardSlides[0].id)
    },

    /**
     */
    showPreviousSlide () {
      const board = this.boardSlides.pop()

      this.boardSlides = [board].concat(this.boardSlides)
      this.saveActiveBoardId(this.boardSlides[0].id)
    },

    /**
     * @param {number} boardId
     */
    saveActiveBoardId (boardId) {
      localStorage.setItem(`activeBoard-${this.projectId}`, boardId)

      this.activeBoardId = boardId
    }
  }
}
</script>

<style lang="scss" scoped>
$size-button-navigation: 50px;

.project__empty {
  @include size(100%);

  display: flex;
  align-items: center;
  justify-content: center;
}

.project__slider {
  display: flex;
  height: 100%;
  overflow: hidden;
}

.project__left,
.project__right,
.project__center {
  @include size(100%);

  position: relative;
  display: flex;
  flex-direction: column;
}

.project__right {
  justify-content: flex-end;
}

.project__boards {
  @include size(100%);

  padding: 0 $gutter;
}

.project__boards-navigation {
  @include absolute-center-left;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.project__board {
  @include size(100%);

  position: relative;
  flex: 0 0 100%;
  padding: $gutter 0;
}
</style>
